import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { SYSTEMS_BY_ID, webEngine, type GameDetail, type SaveSlot } from '@replay/shared';
import { api, ApiError, errorMessage } from '../lib/api.ts';
import { netplay } from '../lib/netplay/session.ts';
import { downloadForOffline, recallGame, rememberGame, useOnline } from '../lib/offline.ts';
import { CacheError } from '@replay/gamecache';
import { OverlayPicker } from './game/OverlayPicker.tsx';
import { useOverlayChoice } from '../player/overlay/useOverlays.ts';
import { getCache, useCacheEntry } from '../lib/cache.ts';
import { ago, bytes, dateTime, systemAccent, systemName } from '../lib/format.ts';
import { useHints, useNavAction } from '../lib/nav/react.tsx';
import { ambientFor, useAmbient } from '../components/Ambient.tsx';
import { Cover, Shelf } from '../components/GameCard.tsx';
import { Icon } from '../components/Icon.tsx';
import { ErrorState, Loading, Modal, useConfirm, useToast } from '../components/ui.tsx';

const COMPAT: Record<string, { label: string; tone: string }> = {
  perfect: { label: 'Funciona perfecto', tone: 'ok' },
  playable: { label: 'Jugable', tone: 'ok' },
  issues: { label: 'Con fallos menores', tone: 'warn' },
  broken: { label: 'No funciona', tone: 'danger' },
  untested: { label: 'Sin probar', tone: '' },
};

function Stars({ value, onChange }: { value: number | null; onChange: (n: number) => void }) {
  return (
    <div className="stars" role="radiogroup" aria-label="Tu valoración" data-nav-row>
      {[1, 2, 3, 4, 5].map((n) => (
        <button key={n} role="radio" aria-checked={value === n} aria-label={`${n} de 5`} className={value && n <= value ? 'on' : ''} onClick={() => onChange(n)}>
          <Icon name="star" />
        </button>
      ))}
    </div>
  );
}

/** Overlay choice, shown only when the game has themed overlays. */
function Appearance({ gameId, screenshot }: { gameId: string; screenshot?: string }) {
  const { overlays } = useOverlayChoice(gameId);
  if (!overlays.length) return null;
  return (
    <div>
      <h2>Apariencia</h2>
      <p className="small muted" style={{ margin: 'var(--s-2) 0 var(--s-3)' }}>
        Elige cómo se ve la pantalla de juego. Se recuerda para este juego.
      </p>
      <OverlayPicker gameId={gameId} screenshot={screenshot} />
    </div>
  );
}

export function GamePage() {
  const { id = '' } = useParams();
  const qc = useQueryClient();
  const toast = useToast();
  const confirm = useConfirm();
  const nav = useNavigate();
  const [hosting, setHosting] = useState(false);
  const playOnline = async (mode: 'pads' | 'link' = 'pads') => {
    setHosting(true);
    try {
      const room = await netplay.host(id, mode);
      nav(`/room/${room.id}`);
    } catch (e) {
      toast(errorMessage(e), 'error');
      setHosting(false);
    }
  };
  // Online: fetch and remember the sheet for offline use. Offline: use the remembered sheet.
  const q = useQuery({
    queryKey: ['game', id],
    networkMode: 'always',
    queryFn: async () => {
      try {
        const g = await api<GameDetail>(`/api/games/${encodeURIComponent(id)}`);
        rememberGame(g);
        return g;
      } catch (e) {
        const cached = e instanceof ApiError && e.retryable ? recallGame(id) : null;
        if (cached) return cached;
        throw e;
      }
    },
  });
  const online = useOnline();
  const [dl, setDl] = useState<{ loaded: number; total: number; ctrl: AbortController } | null>(null);
  const g = q.data;
  const saves = useQuery({ queryKey: ['saves', g?.id], queryFn: () => api<SaveSlot[]>(`/api/games/${g!.id}/saves`), enabled: !!g });
  const cached = useCacheEntry(g?.file?.sha256);
  useAmbient(g ? ambientFor(g, systemAccent) : null);

  const fav = useMutation({
    mutationFn: (on: boolean) => api(`/api/games/${g!.id}/favorite`, { method: on ? 'PUT' : 'DELETE' }),
    onSuccess: (_d, on) => {
      qc.invalidateQueries({ queryKey: ['game', id] });
      qc.invalidateQueries({ queryKey: ['home'] });
      toast(on ? 'Añadido a favoritos' : 'Quitado de favoritos', 'ok');
    },
  });
  const rate = useMutation({
    mutationFn: (score: number) => api(`/api/games/${g!.id}/rating`, { method: 'PUT', body: { score } }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['game', id] }),
    onError: (e) => toast(errorMessage(e), 'error'),
  });

  useHints(g ? [{ button: 'Y', label: g.isFavorite ? 'Quitar favorito' : 'Favorito' }] : null);
  useNavAction((a) => {
    if (a === 'tertiary' && g) {
      fav.mutate(!g.isFavorite);
      return true;
    }
  }, !!g);

  if (q.isLoading) return <Loading />;
  if (q.error || !g)
    return (
      <div className="page">
        <ErrorState error={q.error} onRetry={() => q.refetch()} />
      </div>
    );

  const sys = SYSTEMS_BY_ID[g.systemId];
  const engine = webEngine(g.systemId);
  const compat = g.compatibility ? COMPAT[g.compatibility.status] : null;
  const isCached = cached.data?.state === 'complete';
  const canPlay = !!g.file && engine && (g.file.availability !== 'unavailable' || isCached);
  const hasSave = !!saves.data?.length;

  const removeLocal = async () => {
    if (!g.file) return;
    if (!(await confirm('El juego se borrará de este dispositivo y se volverá a descargar la próxima vez. Tus partidas no se tocan.', { title: 'Quitar del dispositivo', confirmLabel: 'Quitar' }))) return;
    await (await getCache()).remove(g.file.sha256);
    qc.invalidateQueries({ queryKey: ['cache-entry', g.file.sha256] });
    toast('Juego quitado de este dispositivo. Tus partidas siguen guardadas.', 'ok');
  };
  const downloadOffline = async () => {
    if (!g.file || dl) return;
    const ctrl = new AbortController();
    setDl({ loaded: cached.data?.bytesWritten ?? 0, total: g.file.size, ctrl });
    try {
      const r = await downloadForOffline(g, { signal: ctrl.signal, onProgress: (p) => setDl({ loaded: p.loaded, total: p.total, ctrl }) });
      toast(
        r.coreOk ? `${g.title} está listo para jugar sin conexión en este dispositivo.` : 'Juego descargado. El emulador se guardará la primera vez que lo abras con conexión.',
        r.coreOk ? 'ok' : 'info',
      );
    } catch (e) {
      if (!(e instanceof CacheError && e.kind === 'aborted')) toast(errorMessage(e), 'error');
    } finally {
      setDl(null);
      qc.invalidateQueries({ queryKey: ['cache-entry', g.file!.sha256] });
      qc.invalidateQueries({ queryKey: ['local-games'] });
    }
  };

  const downloadSave = async (s: SaveSlot) => {
    try {
      const res = await fetch(`/api/saves/${encodeURIComponent(s.id)}/data`, { credentials: 'same-origin' });
      if (!res.ok) throw new Error('No se ha podido descargar la partida. Inténtalo de nuevo.');
      const blob = await res.blob();
      let name = g.title.replace(/[<>:"/\\|?*\x00-\x1f]/g, '_').replace(/[. ]+$/g, '') || 'partida';
      if (/^(con|prn|aux|nul|com[1-9]|lpt[1-9])(?:\.|$)/i.test(name)) name = `_${name}`;
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      try {
        a.href = url;
        a.download = `${name}.sav`;
        document.body.appendChild(a);
        a.click();
      } finally {
        a.remove();
        setTimeout(() => URL.revokeObjectURL(url), 1000);
      }
    } catch (e) {
      toast(errorMessage(e), 'error');
    }
  };

  const togglePin = async () => {
    if (!g.file || !cached.data) return;
    await (await getCache()).setPinned(g.file.sha256, !cached.data.pinned);
    qc.invalidateQueries({ queryKey: ['cache-entry', g.file.sha256] });
  };

  return (
    <article className="gp">
      <section className={`gp-hero${g.heroUrl ? ' has-art' : ''}`}>
        {g.heroUrl && <div className="hero-art" style={{ backgroundImage: `url("${g.heroUrl}")` }} aria-hidden="true" />}
        <div className="box">
          <Cover game={g} eager />
        </div>
        <div className="gp-info">
          <div className="gp-crumb">
            <span className="sys" style={{ ['--sys' as string]: systemAccent(g.systemId) }}>
              <i /> {systemName(g.systemId)}
            </span>
            {g.year && <span>· {g.year}</span>}
            {g.developer && <span>· {g.developer}</span>}
          </div>
          <h1>{g.title}</h1>
          {g.hack && (
            <p className="gp-hack">
              <Icon name="hack" size={16} /> Hackrom{g.hack.version ? ` v${g.hack.version}` : ''}
              {g.hack.author ? ` de ${g.hack.author}` : ''}
              {g.baseGame && (
                <>
                  {' '}
                  basado en <Link to={`/game/${g.baseGame.slug}`}>{g.baseGame.title}</Link>
                </>
              )}
            </p>
          )}

          <div className="gp-actions">
            {canPlay ? (
              <Link to={`/play/${g.id}`} className="btn btn-primary btn-lg" data-autofocus>
                <Icon name="play" /> {hasSave ? 'Continuar' : 'Jugar'}
              </Link>
            ) : (
              <button className="btn btn-lg" disabled>
                <Icon name="play" /> {!g.file ? 'Sin archivo todavía' : !engine ? 'No compatible con el navegador' : 'No disponible ahora'}
              </button>
            )}
            <button
              className="btn btn-ghost btn-icon btn-lg"
              aria-pressed={!!g.isFavorite}
              aria-label={g.isFavorite ? 'Quitar de favoritos' : 'Añadir a favoritos'}
              onClick={() => fav.mutate(!g.isFavorite)}
            >
              <Icon name="heart" className={g.isFavorite ? 'fill-red' : ''} />
            </button>
            {canPlay && sys?.netplay && (
              <button className="btn btn-ghost btn-lg" disabled={hosting} onClick={() => void playOnline()}>
                <Icon name="friends" /> {hosting ? 'Creando sala…' : 'Jugar online'}
              </button>
            )}
            {canPlay && sys?.link && (
              <button
                className="btn btn-ghost btn-lg"
                disabled={hosting}
                onClick={() => void playOnline('link')}
                title={`Conecta tu ${sys.name} con la de ${sys.linkPlayers && sys.linkPlayers > 2 ? 'tus amigos' : 'un amigo'} para intercambiar o jugar juntos, cada uno con su partida`}
              >
                <Icon name="friends" /> {hosting ? 'Creando sala…' : 'Cable link'}
              </button>
            )}
          </div>

          {dl ? (
            <div className="gp-download" role="status">
              <div className="progress">
                <i style={{ width: `${dl.total ? (dl.loaded / dl.total) * 100 : 0}%` }} />
              </div>
              <span className="small muted">
                Descargando para jugar sin conexión · {bytes(dl.loaded)} de {bytes(dl.total)}
              </span>
              <button className="btn btn-ghost btn-sm" onClick={() => dl.ctrl.abort()}>
                Cancelar
              </button>
            </div>
          ) : (
            g.file &&
            !(isCached && cached.data?.pinned) &&
            online && (
              <div className="row">
                <button className="btn btn-ghost" onClick={() => void downloadOffline()}>
                  <Icon name="download" /> {isCached ? 'Conservar para jugar sin conexión' : 'Descargar para jugar sin conexión'}
                </button>
              </div>
            )
          )}
          <p className="gp-device">
            {isCached ? (
              <>
                <Icon name="check" /> {cached.data?.pinned ? 'Disponible sin conexión' : 'Descargado en este dispositivo'} · {bytes(cached.data!.size)}
              </>
            ) : cached.data?.state === 'partial' ? (
              <>
                Descarga a medias ({bytes(cached.data.bytesWritten)} de {bytes(cached.data.size)}). Al jugar continuará donde se quedó.
              </>
            ) : g.file ? (
              <>Se descargará una vez ({bytes(g.file.size)}) y quedará en este dispositivo.</>
            ) : null}
          </p>
          {g.file?.availability === 'unavailable' && (
            <p className="notice">
              {isCached
                ? 'El almacenamiento de este juego no responde, pero lo tienes descargado y puedes jugar igualmente.'
                : 'El almacenamiento donde está este juego no responde ahora mismo. Vuelve a intentarlo más tarde.'}
            </p>
          )}

          <div className="gp-facts">
            <div className="fact">
              <span>Jugadores</span>
              <b>{g.players ? (g.players > 1 ? `1–${g.players} locales` : '1') : '—'}</b>
            </div>
            {g.genres.length > 0 && (
              <div className="fact">
                <span>Género</span>
                <b>{g.genres.slice(0, 2).join(', ')}</b>
              </div>
            )}
            <div className="fact">
              <span>Online</span>
              <b>{g.onlineSupport === 'none' ? 'No' : g.onlineSupport === 'netplay' ? 'Experimental' : 'Servicio original'}</b>
            </div>
            <div className="fact">
              <span>Compatibilidad</span>
              <b>{compat ? compat.label : 'Sin probar'}</b>
            </div>
            <div className="fact">
              <span>Valoración</span>
              <b>{g.ratingSummary.average ? `${g.ratingSummary.average} / 5 · ${g.ratingSummary.count}` : '—'}</b>
            </div>
          </div>
        </div>
      </section>

      <section className="gp-body">
        <div className="stack" style={{ gap: 'var(--s-6)', minWidth: 0 }}>
          {g.description ? <p className="desc">{g.description}</p> : <p className="muted">Este juego todavía no tiene descripción.</p>}

          {g.screenshots.length > 0 && (
            <div>
              <h2>Capturas</h2>
              <div className="shots" data-nav-row>
                {g.screenshots.map((s, i) => (
                  <a key={s} href={s} target="_blank" rel="noreferrer" aria-label={`Captura ${i + 1}`}>
                    <img src={s} alt="" loading="lazy" />
                  </a>
                ))}
              </div>
            </div>
          )}

          <div>
            <h2>Controles</h2>
            <p className="muted prose" style={{ marginTop: 'var(--s-2)' }}>
              {g.controlsInfo ||
                'Los controles se adaptan solos al teclado, al mando (cruceta o stick) o a la pantalla táctil. Durante la partida, mantén Select en el mando o pulsa Esc para abrir el menú de REPLAY, y F1 muestra u oculta la leyenda de controles.'}
            </p>
            {sys?.dualScreen && (
              <p className="muted prose" style={{ marginTop: 'var(--s-2)' }}>
                Nintendo DS: la pantalla inferior es táctil; en ordenador usa el ratón. Puedes cambiar la disposición de pantallas desde el menú del emulador.
              </p>
            )}
            {engine?.notes && <p className="small faint" style={{ marginTop: 'var(--s-2)' }}>{engine.notes}</p>}
          </div>

          <Appearance gameId={g.id} screenshot={g.screenshots[0]} />

          {g.hack?.changes && (
            <div>
              <h2>Qué cambia este hack</h2>
              <p className="muted prose pre" style={{ marginTop: 'var(--s-2)' }}>
                {g.hack.changes}
              </p>
            </div>
          )}

          {g.collections.length > 0 && (
            <div>
              <h2>En las colecciones</h2>
              <div className="row" style={{ marginTop: 'var(--s-3)' }} data-nav-row>
                {g.collections.map((c) => (
                  <Link className="chip" key={c.id} to={`/collections/${c.slug}`}>
                    {c.name}
                  </Link>
                ))}
              </div>
            </div>
          )}
        </div>

        <aside className="gp-side">
          <div className="panel">
            <h2>Tus partidas</h2>
            {saves.data?.length ? (
              <div className="list">
                {saves.data.map((s) => (
                  <div className="list-item" key={s.id} title={dateTime(s.updatedAt)}>
                    <Icon name={s.kind === 'sram' ? 'save' : 'history'} />
                    <div className="grow">
                      <strong className="small">{s.kind === 'sram' ? 'Partida del juego' : `Estado rápido ${s.slot + 1}`}</strong>
                      <div className="tiny faint">
                        {ago(s.updatedAt)} · {s.deviceName ?? 'otro dispositivo'} · v{s.headVersion}
                      </div>
                    </div>
                    {s.kind === 'sram' && (
                      <button className="btn btn-ghost btn-icon btn-sm" title="Descargar" aria-label="Descargar partida" onClick={() => void downloadSave(s)}>
                        <Icon name="download" />
                      </button>
                    )}
                  </div>
                ))}
                <p className="tiny faint" style={{ marginTop: 'var(--s-2)' }}>
                  Puedes descargar tu partida para guardarla tú. Por seguridad, REPLAY no permite subir partidas de fuera.
                </p>
                <Link to="/settings/saves" className="link-btn small" style={{ marginTop: 'var(--s-2)' }}>
                  Historial y restaurar versiones
                </Link>
              </div>
            ) : (
              <p className="small muted">Cuando juegues, tu progreso se guardará solo y estará en todos tus dispositivos.</p>
            )}
          </div>
          <div className="panel">
            <h2>Tu valoración</h2>
            <Stars value={g.ratingSummary.mine} onChange={(n) => rate.mutate(n)} />
          </div>
          {isCached && (
            <div className="panel">
              <h2>En este dispositivo</h2>
              <div className="stack">
                <button className="btn btn-ghost btn-sm" aria-pressed={!!cached.data?.pinned} onClick={togglePin}>
                  <Icon name="pin" /> {cached.data?.pinned ? 'Se conserva siempre' : 'Conservar siempre'}
                </button>
                <button className="btn btn-ghost btn-sm" onClick={removeLocal}>
                  <Icon name="trash" /> Quitar de este dispositivo
                </button>
              </div>
            </div>
          )}
          {compat && g.compatibility?.notes && (
            <div className="panel">
              <h2>Notas de compatibilidad</h2>
              <p className="small muted">{g.compatibility.notes}</p>
            </div>
          )}
        </aside>
      </section>

      {g.variants.length > 0 && (
        <section className="rail-section" aria-label="Versiones y hacks">
          <div className="rail-head">
            <h2>Versiones y hacks</h2>
          </div>
          <Shelf games={g.variants} label="Versiones y hacks" />
        </section>
      )}
      <div style={{ height: 'var(--s-8)' }} />

    </article>
  );
}
